import oidcTemplateUrl from '../../../app/oidc/oidc.tpl.html';
import { memoize } from 'lodash';
import { LOGIN_TYPES, startLoginWorkflow } from '../../common/security/login/loginTelemetry';

const validTokenLengths = [2, 3];

/**
 * Exchanges an OIDC token for a User token (once only).
 * Token exchange is followed by redirecting user to correct location.
 *
 * Successive call with same OIDC token will return the
 * response of very first call.
 */
const processOidcTokenOnce = memoize(function (
    token,
    {
        $http,
        $q,
        $log,
        $location,
        $window,
        security,
        loginService,
        ssoRedirect,
        DOMAIN_ORGANIZATION_ID,
        API_V2_URL,
        auth,
        currentUser,
        oauthBrokerContinuation,
    }
) {
    const loginWorkflow = startLoginWorkflow(LOGIN_TYPES.OIDC_SSO);
    const brokerInspection = oauthBrokerContinuation.inspect();

    function exchangeSessionWithToken() {
        if (!token) {
            $log.error('Missing OIDC token');
            return $q.reject(null);
        }
        const tokenParts = token.split(':::');
        function respond() {
            if (validTokenLengths.includes(tokenParts.length)) {
                const payload = {
                    code: tokenParts[0],
                    orgId: DOMAIN_ORGANIZATION_ID,
                    integrationId: tokenParts[1],
                };
                if (tokenParts.length === 3) {
                    payload.orgUserId = tokenParts[2];
                }
                return $http({
                    method: 'POST',
                    url: API_V2_URL + '/session/oidc/exchange',
                    data: payload,
                });
            } else {
                $log.error('Invalid OIDC token for session exchange');
                return $q.reject(null);
            }
        }
        return security.logout().then(respond, respond);
    }

    if (brokerInspection.owned && !brokerInspection.active) {
        loginWorkflow.end(false);
        $location.url('/oauth-authorize');
        return $q.when();
    }

    return exchangeSessionWithToken()
        .then((result) => {
            const sessionToken =
                result && (result.data?.accessToken || result.data?.sf_accessToken);
            if (sessionToken && auth.authToken() !== sessionToken) {
                auth.setAuth(sessionToken);
            }
            currentUser.clearUserCache();
            return loginService.initOrgAndUserPostLogin();
        })
        .then(() => {
            loginWorkflow.end(true);
            if (brokerInspection.owned) {
                $location.url('/oauth-authorize');
                return;
            }

            const url = ssoRedirect.getAndClear();
            if (url) {
                $location.url(url);
            } else {
                $location.path('/home');
            }
        })
        .catch(() => {
            loginWorkflow.end(false);
            if (brokerInspection.owned) {
                oauthBrokerContinuation.setTerminal('unknown');
                $location.url('/oauth-authorize');
                return;
            }

            $window.alert('Sign-in failed. Error: Invalid token.');
            $log.error('Error exchanging OIDC token');
            $location.path('/signin');
        });
});

const OidcController = [
    '$http',
    '$scope',
    '$q',
    '$log',
    '$location',
    '$window',
    'security',
    'loginService',
    'ssoRedirect',
    'DOMAIN_ORGANIZATION_ID',
    'API_V2_URL',
    'auth',
    'currentUser',
    'oauthBrokerContinuation',
    function (
        $http,
        $scope,
        $q,
        $log,
        $location,
        $window,
        security,
        loginService,
        ssoRedirect,
        DOMAIN_ORGANIZATION_ID,
        API_V2_URL,
        auth,
        currentUser,
        oauthBrokerContinuation
    ) {
        const $ctrl = this;

        $scope.processOidcToken = (token) =>
            processOidcTokenOnce(token, {
                $http,
                $q,
                $log,
                $location,
                $window,
                security,
                loginService,
                ssoRedirect,
                DOMAIN_ORGANIZATION_ID,
                API_V2_URL,
                auth,
                currentUser,
                oauthBrokerContinuation,
            });

        $ctrl.$onInit = () => {
            $scope.processOidcToken($ctrl.token);
        };
    },
];

// there is no ui for the oidc token exchange
// it's just a redirect to homepage
// with an intermediate step of exchanging
// the identity token for an access token
export default {
    bindings: { token: '<' },
    templateUrl: oidcTemplateUrl,
    controller: OidcController,
};
